// =====================================================
// CHAT PURE UTILITIES
// Framework-free helpers extracted verbatim from the original Chat monolith
// (plus a few small additions for the new features). Keeping them pure makes
// them trivially testable and keeps the container focused on orchestration.
// =====================================================

import axios from "axios";

// Unique client-side id for a message. Used as an idempotency key so a retry
// after a dropped connection can never create a duplicate on the server, and
// so the optimistic "sending" bubble can be reconciled with the saved doc.
export const makeClientId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Delivery-status ordering so a slower/older event can never DOWNGRADE a
// message's tick (e.g. a late "sent" echo arriving after "read").
export const STATUS_RANK = {
  sending: 0,
  failed: 0,
  sent: 1,
  delivered: 2,
  read: 3,
};

// Insert or reconcile a message into the list. Matches an existing entry by
// server _id first, then by clientId (which covers the optimistic placeholder
// before it has an _id). Merges rather than duplicates, and keeps the highest
// delivery status seen. This is the single source of truth for "add a message"
// so the optimistic send, the REST response and the socket relay all converge
// on ONE bubble instead of racing into duplicates.
export const upsertMessage = (list, incoming) => {
  const idx = list.findIndex(
    (m) =>
      (incoming._id && String(m._id) === String(incoming._id)) ||
      (incoming.clientId && m.clientId && m.clientId === incoming.clientId)
  );
  if (idx === -1) return [...list, incoming];

  const prev = list[idx];
  const keepPrevStatus =
    (STATUS_RANK[prev.status] ?? 0) > (STATUS_RANK[incoming.status] ?? 0);
  const next = list.slice();
  next[idx] = {
    ...prev,
    ...incoming,
    status: keepPrevStatus ? prev.status : incoming.status || prev.status,
  };
  return next;
};

// POST with a timeout and ONE automatic retry for TRANSIENT failures only
// (no HTTP response: network drop, timeout, a Render free-tier cold start).
// A real HTTP response — including 4xx/5xx — is returned to the caller
// immediately and never retried. The retry is safe because every send carries
// an idempotent clientId, so a request the server already saved won't be
// duplicated.
export const postWithRetry = async (url, body, config = {}) => {
  const cfg = { timeout: 20000, ...config };
  try {
    return await axios.post(url, body, cfg);
  } catch (err) {
    if (!err.response) {
      console.warn(
        "[chat] transient network error — retrying once:",
        url,
        err.message
      );
      return await axios.post(url, body, cfg);
    }
    throw err;
  }
};

// Format a duration in seconds as m:ss (used by voice messages / players).
export const formatDuration = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// Short, human label describing what a message contains — used for chat-list
// previews, reply snippets and forward summaries. Never returns undefined.
export const messagePreview = (m) => {
  if (!m) return "";
  if (m.text) return m.text;
  if (m.fileType?.startsWith("image")) return "📷 Photo";
  if (m.fileType?.startsWith("video")) return "🎥 Video";
  if (m.fileType?.startsWith("audio")) return "🎤 Voice message";
  if (m.fileUrl) return `📎 ${m.fileName || "File"}`;
  return "";
};

// Format a timestamp as a short local time (HH:MM). Safe on missing input.
export const formatTime = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};
